// Design System Tokens

// Originally from SpendPlan v2 (`SpendPlan/utils/designSystem.ts`) — promoted
// to src/ during the v2→v1 merge so v1 code can use the same tokens. Lives
// alongside `theme.ts` (the MUI theme).
//
// What lives where:
//   - `theme.ts`        → MUI theme: palette, typography (h1–h6), breakpoints,
//                         component overrides. Use `theme.palette.*`,
//                         `theme.spacing(n)`, `theme.shape.borderRadius` for
//                         standard MUI styling.
//   - `designSystem.ts` → Semantic tokens (this file): app-specific scales for
//                         radius/spacing/typography/shadows + composite recipe
//                         objects (cardStyles, gradients, iconBoxStyles,
//                         listItemStyles, statusIndicator, progressBarStyles).
//   - `colors.ts`       → Domain palettes: COLORS, GRADIENTS (per-entity
//                         accents), categoryColors, fundFlowColors.

import { alpha, Theme } from '@mui/material';

// Typography Scale

export const typography = {
	// Display - Hero numbers and primary KPIs
	displayLarge: {
		fontSize: '4.5rem',
		fontWeight: 800,
		lineHeight: 1.1,
		letterSpacing: '-0.03em',
	},
	displayMedium: {
		fontSize: '3.5rem',
		fontWeight: 700,
		lineHeight: 1.15,
		letterSpacing: '-0.02em',
	},
	displaySmall: {
		fontSize: '2.75rem',
		fontWeight: 700,
		lineHeight: 1.2,
		letterSpacing: '-0.02em',
	},

	// Headings - INCREASED for better visual anchoring
	h1: {
		fontSize: '2.5rem',
		fontWeight: 700,
		lineHeight: 1.3,
		letterSpacing: '-0.01em',
	},
	h2: {
		fontSize: '2rem',
		fontWeight: 700,
		lineHeight: 1.35,
		letterSpacing: '-0.01em',
	},
	h3: {
		fontSize: '1.75rem',
		fontWeight: 600,
		lineHeight: 1.4,
	},
	h4: {
		fontSize: '1.5rem',
		fontWeight: 600,
		lineHeight: 1.4,
	},

	// Body text - MINIMUM 1.25rem (20px) for readability
	bodyLarge: {
		fontSize: '1.5rem',
		fontWeight: 400,
		lineHeight: 1.7,
	},
	body: {
		fontSize: '1.25rem',
		fontWeight: 400,
		lineHeight: 1.7,
	},
	bodyMedium: {
		fontSize: '1.25rem',
		fontWeight: 500,
		lineHeight: 1.6,
	},
	bodyBold: {
		fontSize: '1.25rem',
		fontWeight: 600,
		lineHeight: 1.6,
	},

	// Labels - INCREASED for readability
	label: {
		fontSize: '1.125rem',
		fontWeight: 600,
		lineHeight: 1.5,
		letterSpacing: '0.02em',
		color: '#374151', // Darker gray for better contrast
	},
	labelSmall: {
		fontSize: '1rem',
		fontWeight: 700,
		lineHeight: 1.5,
		letterSpacing: '0.05em',
		textTransform: 'uppercase' as const,
	},

	// Values - for currency/numbers - INCREASED
	valueLarge: {
		fontSize: '2.5rem',
		fontWeight: 700,
		lineHeight: 1.2,
		fontFeatureSettings: '"tnum"',
	},
	value: {
		fontSize: '1.875rem',
		fontWeight: 600,
		lineHeight: 1.3,
		fontFeatureSettings: '"tnum"',
	},
	valueSmall: {
		fontSize: '1.5rem',
		fontWeight: 600,
		lineHeight: 1.4,
		fontFeatureSettings: '"tnum"',
	},
} as const;

// Spacing Scale

export const spacing = {
	xs: 1, // 8px - increased from 4px
	sm: 1.5, // 12px - increased from 8px
	md: 2.5, // 20px - increased from 16px
	lg: 4, // 32px - increased from 24px
	xl: 5, // 40px - increased from 32px
	xxl: 6, // 48px - increased from 40px
	xxxl: 8, // 64px - increased from 48px
	// Section spacing for visual anchoring
	section: 6, // 48px - space between major sections
	card: 3, // 24px - internal card padding
} as const;

// Border Radius

export const radius = {
	sm: 2, // 8px - subtle rounding
	md: 3, // 12px - cards
	lg: 4, // 16px - large cards
	xl: 5, // 20px - featured sections
	full: 100, // pill shape
} as const;

// Shadows

export const shadows = {
	// Subtle elevation
	sm: '0 1px 2px rgba(0,0,0,0.04)',
	// Card shadow
	card: '0 2px 8px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.02)',
	// Elevated card
	cardHover: '0 8px 24px rgba(0,0,0,0.08), 0 16px 32px rgba(0,0,0,0.04)',
	// Featured/Hero sections
	featured: '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
	// Icon shadows (colored)
	icon: (color: string) => `0 4px 12px ${alpha(color, 0.35)}`,
} as const;

// Card Styles

export const cardStyles = {
	// Standard card - SIMPLIFIED: white bg with subtle border
	base: (theme: Theme) => ({
		bgcolor: '#ffffff',
		borderRadius: radius.md,
		border: '1px solid',
		borderColor: '#e5e7eb', // Consistent light gray border
		boxShadow: 'none', // Remove shadow for cleaner look
		transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
	}),

	// Interactive card with hover
	interactive: (theme: Theme) => ({
		...cardStyles.base(theme),
		cursor: 'pointer',
		'&:hover': {
			borderColor: theme.palette.primary.main,
			backgroundColor: '#f9fafb',
		},
	}),

	// Featured/Hero card - SIMPLIFIED: white bg with accent left border
	featured: (theme: Theme) => ({
		bgcolor: '#ffffff',
		borderRadius: radius.md,
		border: '1px solid',
		borderColor: '#e5e7eb',
		boxShadow: 'none',
		position: 'relative' as const,
		overflow: 'hidden' as const,
	}),

	// Section card - NEW: white bg with colored left accent border
	section: (theme: Theme, accentColor: string) => ({
		bgcolor: '#ffffff',
		borderRadius: radius.md,
		border: '1px solid',
		borderColor: '#e5e7eb',
		borderLeft: `4px solid ${accentColor}`,
		boxShadow: 'none',
		position: 'relative' as const,
	}),

	// Glass effect card - KEPT but simplified
	glass: (theme: Theme) => ({
		bgcolor: '#ffffff',
		borderRadius: radius.md,
		border: '1px solid',
		borderColor: '#e5e7eb',
	}),
} as const;

// Gradient Presets

export const gradients = {
	// Primary gradient for icons/accents
	primary: (theme: Theme) =>
		`linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,

	// Success gradient
	success: (theme: Theme) =>
		`linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,

	// Info gradient
	info: (theme: Theme) =>
		`linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,

	// Warning gradient
	warning: (theme: Theme) =>
		`linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,

	// Hero accent bar (rainbow)
	heroAccent: (theme: Theme) =>
		`linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.success.main})`,

	// Subtle background gradient
	subtleBackground: (theme: Theme) =>
		`linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,

	// Radial glow for backgrounds
	radialGlow: (color: string, opacity = 0.06) =>
		`radial-gradient(circle at 100% 0%, ${alpha(color, opacity)}, transparent 70%)`,
} as const;

// Icon Box Styles

export const iconBoxStyles = {
	// Standard icon container
	standard: (theme: Theme, color: string) => ({
		alignItems: 'center',
		bgcolor: alpha(color, 0.12),
		borderRadius: radius.md,
		display: 'flex',
		height: 56,
		justifyContent: 'center',
		width: 56,
	}),

	// Gradient icon with shadow
	gradient: (gradient: string, shadowColor: string) => ({
		alignItems: 'center',
		background: gradient,
		borderRadius: radius.md,
		boxShadow: shadows.icon(shadowColor),
		display: 'flex',
		height: 56,
		justifyContent: 'center',
		width: 56,
	}),

	// Large gradient icon
	gradientLarge: (gradient: string, shadowColor: string) => ({
		alignItems: 'center',
		background: gradient,
		borderRadius: radius.lg,
		boxShadow: shadows.icon(shadowColor),
		display: 'flex',
		height: 64,
		justifyContent: 'center',
		width: 64,
	}),
} as const;

// List Item Styles

export const listItemStyles = {
	// Standard list row
	row: (theme: Theme) => ({
		py: spacing.sm,
		transition: 'background-color 0.15s ease',
		'&:hover': {
			bgcolor: alpha(theme.palette.primary.main, 0.03),
		},
	}),

	// Row with border
	rowBordered: (theme: Theme, isFirst: boolean) => ({
		...listItemStyles.row(theme),
		borderTop: isFirst ? 'none' : '1px solid',
		borderColor: theme.palette.divider,
	}),

	// Row with dotted leader line between name and value
	rowWithLeader: (theme: Theme) => ({
		py: spacing.sm,
		display: 'flex',
		alignItems: 'center',
		gap: spacing.md,
		'& .leader': {
			flex: 1,
			height: 0,
			borderBottom: '1px dotted',
			borderColor: alpha(theme.palette.text.secondary, 0.3),
			minWidth: spacing.lg,
		},
	}),

	// Section header - simple, no gradient
	sectionHeader: (theme: Theme) => ({
		py: 1.5,
		borderBottom: '1px solid',
		borderColor: theme.palette.divider,
	}),
} as const;

// Status Indicator Styles

export const statusIndicator = {
	// Colored dot
	dot: (color: string, size = 10) => ({
		bgcolor: color,
		borderRadius: '50%',
		height: size,
		width: size,
		boxShadow: `0 0 0 3px ${alpha(color, 0.2)}`,
	}),

	// Square chip
	chip: (color: string, size = 12) => ({
		bgcolor: color,
		borderRadius: 1,
		height: size,
		width: size,
		boxShadow: `0 2px 4px ${alpha(color, 0.4)}`,
	}),
} as const;

// Progress Bar Styles

export const progressBarStyles = {
	// Track (background)
	track: (color: string) => ({
		height: 6,
		bgcolor: alpha(color, 0.15),
		borderRadius: 3,
		overflow: 'hidden',
	}),

	// Fill
	fill: (color: string, percentage: number) => ({
		height: '100%',
		width: `${percentage}%`,
		bgcolor: color,
		borderRadius: 3,
		transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
	}),
} as const;
